// The build-time copy of a §'s generated tables (./forms.ts): /data/grammar/<theme>.json, written by
// `npm run build:grammar` and precached by the service worker with the other small data files
// (revisioned by content, vite.config.ts). Loaded with the § body (grammar/index.ts
// loadGrammarTheme); a file that is missing, slow, broken or from another build is simply not used:
// the § then generates its forms as before.
import { installGrammarTables, type GrammarTables } from './table-store';

/** The hash a § file must carry to be used (vite.config.ts; app/scripts/grammar-hash.ts). */
export const GRAMMAR_HASH: string = typeof __GRAMMAR_HASH__ === 'string' ? __GRAMMAR_HASH__ : '';

/** The URL of a §'s file. */
export const grammarTablesUrl = (themeId: string): string => `/data/grammar/${encodeURIComponent(themeId)}.json`;

/** Is `x` a § file of this build for `themeId`? */
export function usableTables(x: unknown, themeId: string, hash = GRAMMAR_HASH): x is GrammarTables {
  if (!x || typeof x !== 'object') return false;
  const t = x as Partial<GrammarTables>;
  return t.v === 1 && t.theme === themeId && !!hash && t.engine === hash && !!t.words && typeof t.words === 'object';
}

/** A § longer than this to arrive is not waited for (the page generates its forms instead). */
const WAIT_MS = 2500;

const tried = new Map<string, Promise<boolean>>();

/** Load and install one §'s tables (once per page load); true when they are in use. Never throws. */
export function loadGrammarTables(themeId: string): Promise<boolean> {
  let p = tried.get(themeId);
  if (p) return p;
  p = (async () => {
    if (!GRAMMAR_HASH || typeof fetch !== 'function') return false;
    try {
      const ctl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = setTimeout(() => ctl?.abort(), WAIT_MS);
      try {
        const res = await fetch(grammarTablesUrl(themeId), { credentials: 'same-origin', ...(ctl ? { signal: ctl.signal } : {}) });
        if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return false;
        const data: unknown = await res.json();
        if (!usableTables(data, themeId)) return false;
        installGrammarTables(data);
        return true;
      } finally {
        clearTimeout(timer);
      }
    } catch {
      return false;
    }
  })();
  tried.set(themeId, p);
  return p;
}

/**
 * Every §'s tables, for a warm-up before the app is used (the startup screen): the files come from
 * the service worker's precache; afterwards opening a § only renders. Never throws.
 */
export async function preloadGrammarTables(themeIds: readonly string[]): Promise<number> {
  const ok = await Promise.all(themeIds.map((id) => loadGrammarTables(id)));
  return ok.filter(Boolean).length;
}

/** Forget what was tried (tests). */
export function resetGrammarTablesLoad(): void {
  tried.clear();
}
